import { useCallback } from 'react';

import type { PaymentRequest, StripePaymentRequestButtonElementClickEvent } from '@stripe/stripe-js';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { PaymentType } from '#src/app/shared/utils/payments';

type Params = {
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	paymentRequest: PaymentRequest | null;
	paymentType: PaymentType;
	validate?: (paymentType: PaymentType) => Promise<boolean>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
};

export function useOnClick({
	beforeToken,
	paymentRequest,
	paymentType,
	validate,
	prePaymentChecks,
}: Params): (e: StripePaymentRequestButtonElementClickEvent) => Promise<void> {
	const track = useTracking();

	return useCallback(
		async (e) => {
			e.preventDefault();
			if (!(await beforeToken(paymentType))) return;

			if (validate && !(await validate(paymentType))) return;

			const showPaymentSheet = () => {
				if (!paymentRequest) return;
				paymentRequest.show();
				void track('payment_request_show', { payment_type: paymentType });
			};

			if (paymentType === 'applePay') {
				// Apple Pay must call show() synchronously inside Safari's gesture window, before
				// prePaymentChecks can resolve — so payment_request_show fires on every tap (the user
				// did see the sheet open). When the checks then fail we abort the sheet and fire
				// payment_request_abort, so downstream can exclude these from wallet-attempt metrics
				// (true attempts = payment_request_show − payment_request_abort). The Google Pay branch
				// below runs the checks first and only fires show once they pass, so it never aborts.
				showPaymentSheet();
				if (prePaymentChecks && !(await prePaymentChecks(paymentType))) {
					void track('payment_request_abort', { payment_type: paymentType });
					paymentRequest?.abort();
				}
			} else {
				if (prePaymentChecks && !(await prePaymentChecks(paymentType))) {
					return;
				}
				showPaymentSheet();
			}
		},
		[beforeToken, paymentRequest, paymentType, validate, prePaymentChecks, track],
	);
}
